import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { Box, Button, Card, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { SnackbarKey, useSnackbar } from 'notistack';
import { Report, FmtpError, FmtpErrorCode, ReportType } from '@lanterds/types';
import './ManageReports.scss';
import { ReportForm, ReportTypeMap, reportTypeMapObj } from './types';
import { getReports } from 'src/api/reports';
import EditReportDialog from './EditReportDialog';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';

export default function ManageReports() {
    const [createReportDialogOpen, setCreateReportDialogOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState<ReportForm | null>(null);
    const [isSavingReport, setIsSavingReport] = useState(false);
    const [reports, setReports] = useState<ReportForm[] | null>(null);
    const [isFetchingReports, setIsFetchingReports] = useState(false);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const snackbarRef = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        return () => {
            if (snackbarRef.current) {
                closeSnackbar(snackbarRef.current);
                snackbarRef.current = null;
            }
        };
    }, [closeSnackbar]);

    useEffect(() => {
        if (!reports && !isFetchingReports) {
            getReportList();
        }
    }, [reports, isFetchingReports, isSavingReport]);

    const mapData = (reports: Report[]): ReportForm[] => {
        return reports.map((report) => {
            const tempReport: Partial<ReportForm> = {
                displayPermission: `reports_${report.reportName}`,
            };

            if (report.sigmaReportName) {
                tempReport.displayName = report.sigmaReportName;
                tempReport.reportType = ReportType.Sigma;
            } else if (report.powerBiReportName) {
                tempReport.displayName = report.sigmaReportName;
                tempReport.reportType = ReportType.PowerBi;
                if (report.powerBiDatasets) {
                    tempReport.reportType = ReportType.PowerBiPaginated;
                }
            } else if (report.fmtpRoute) {
                tempReport.displayName = report.reportDisplayName;
                tempReport.reportType = ReportType.Fmtp;
            }

            return { ...report, ...tempReport } as ReportForm;
        });
    };

    const getReportList = async () => {
        snackbarRef.current = enqueueSnackbar('Getting reports...', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        setIsFetchingReports(true);

        getReports()
            .then((data) => {
                const mappedData = mapData(data);

                setReports(
                    mappedData.sort((a: ReportForm, b: ReportForm) =>
                        a.reportName.toLocaleLowerCase().localeCompare(b.reportName.toLocaleLowerCase()),
                    ),
                );
            })
            .catch((err) => {
                const message = 'An error occurred while getting report config list';
                const errorCode: FmtpErrorCode = 'SHU018';
                console.error(err);
                throw new FmtpError({
                    message,
                    errorCode,
                });
            })
            .finally(() => {
                if (snackbarRef.current) {
                    closeSnackbar(snackbarRef.current);
                    snackbarRef.current = null;
                }
                setIsFetchingReports(false);
            });
    };

    const handleOpenCreateReportDialog = () => {
        setCreateReportDialogOpen(true);
    };

    const handleCloseCreateReportDialog = () => {
        setCreateReportDialogOpen(false);
        setSelectedReport(null);
    };

    const onReportClick = (report: ReportForm) => {
        setSelectedReport(report);
        setCreateReportDialogOpen(true);
    };

    const handleReportCreated = async () => {
        handleCloseCreateReportDialog();
        closeSnackbar();
        getReportList();
    };

    const columns: ColumnDef<ReportForm>[] = [
        {
            accessorKey: 'reportName',
            header: 'FMTP ID',
            meta: {
                innerHtml: ({ row, getValue }: CellContext<ReportForm, unknown>) => {
                    return (
                        <Typography onClick={() => onReportClick(row.original)} className="name-cell-text">
                            {getValue() as ReactNode}
                        </Typography>
                    );
                },

                headerHtml: (_props, displayText) => {
                    return (
                        <div>
                            {displayText}
                            <Tooltip arrow title={'FMTP ID is used as the primary key.'} placement="top">
                                <InfoTwoToneIcon />
                            </Tooltip>
                        </div>
                    );
                },
            },
        },
        {
            accessorKey: 'reportDisplayName',
            header: 'Display Name',
        },
        {
            accessorKey: 'reportGroup',
            header: 'Display Group',
        },
        {
            accessorKey: 'reportType',
            header: 'Type',
            meta: {
                innerHtml: ({ getValue }: CellContext<ReportForm, unknown>) => {
                    const type = getValue() as ReportType;
                    return reportTypeMapObj[type];
                },
            },
        },
    ];

    return (
        <Box id="manage-reports-container" className="auto-fill-height">
            <Card id="manage-reports-card" className="auto-fill-height">
                <Grid container spacing={2}>
                    <Grid xs={12} className="manage-reports-header">
                        <Typography variant="h5">Reports</Typography>
                    </Grid>
                    <Grid xs={12} container className="user-buttons-grid">
                        <Grid>
                            <Button variant="contained" size="medium" onClick={handleOpenCreateReportDialog}>
                                Create New Report
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <FmtpTable
                    data={reports ?? []}
                    columns={columns}
                    enableSorting={true}
                    isResizable={true}
                    enableFilters={true}
                    isFetching={isFetchingReports || (!isFetchingReports && !reports)}
                    showTable={!isFetchingReports && !!reports}
                />
            </Card>
            <EditReportDialog
                open={createReportDialogOpen}
                onClose={handleCloseCreateReportDialog}
                onReportCreated={handleReportCreated}
                reportInfo={selectedReport}
                isEditing={!!selectedReport}
                setIsSavingReport={setIsSavingReport}
                reportTypeMapObj={reportTypeMapObj}
            />
        </Box>
    );
}
